'use client';

import classNames from 'classnames';
import { useEffect, useState } from 'react';
import React from 'react';
import SkeletonLoader from '@/components/atoms/skeleton-loader/skeleton-loader';
import Typography from '@/components/atoms/typography/typography';
import BookingExtraSelectModal from '@/components/modules/booking-extra/booking-extra-select-modal/booking-extra-select-modal';
import AlertBanner from '@/components/molecules/alert-banner/alert-banner';
import AlertPill from '@/components/molecules/alert-pill/alert-pill';
import Button from '@/components/molecules/button/button';
import Select, { SelectOption } from '@/components/molecules/select/select';
import Tag, { TagVariant } from '@/components/molecules/tag/tag';
import ExtraCardModal from '@/components/organisms/extra-card-modal/extra-card-modal';
import ImageSlider from '@/components/organisms/image-slider/image-slider';
import PriceItems from '@/components/organisms/price-items/price-items';
import SelectedPassengers from '@/components/organisms/selected-passengers/selected-passengers';
import TruncatedText from '@/components/organisms/truncated-text/truncated-text';
import Link from '@/components/utilities/link/link';
import { ModuleWrapperProps } from '@/components/utilities/module-wrapper/module-wrapper';
import useMutationPatchPreBookingProduct from '@/integrations/booking/pre-booking/products/patch-product/patch-product.hooks';
import { QueryId } from '@/integrations/content/utils';
import { useI18n } from '@/libraries/contexts/i18n-context';
import { usePageDetailsContext } from '@/libraries/contexts/page-details-context/page-details-context';
import { useWindowSize } from '@/libraries/hooks/use-window-size';
import BookingExtraCustomSelectModal from './booking-extra-custom-select-modal/booking-extra-custom-select-modal';
import {
  generatePatchProductBodyRequestAddForAllMultipleSelect,
  generatePatchProductBodyRequestAddForAllSingleSelect,
  generatePatchProductBodyRequestDeleteForAllMultipleSelect,
  getAvailableProductsForPassengers,
  getSelectionChangesMultipleChoice,
} from './booking-extra-utils/booking-extra-utils';
import styles from './booking-extra.module.scss';

export interface BookingExtraProps extends ModuleWrapperProps {
  /** An event that is triggered on click add for all upsell product */
  addForAllUpsellProduct?: () => void;
  /** The class name to apply */
  className?: string;
  /** Generic product data */
  genericProduct?: Content.Components['GenericProductModuleDto']['genericProduct'];
  /** Indicates whether to group by product allotment days */
  groupByProductAllotmentDays?: boolean;
  /** Module id */
  id?: Content.Components['GenericProductModuleDto']['id'];
  /** Indicates whether the product is a child product */
  isChildProduct?: boolean;
  /** Whether the card is fetching */
  isFetching?: boolean;
  /** Indicates whether the product is a golf package */
  isGolfPackage?: boolean;
  /** Whether the card is loading */
  isLoading?: boolean;
  /** Indicates whether the product is a multi generic product */
  isMultiGenericProduct?: boolean;
  /** The overlay description */
  overlayDescription?: string;
  /** The overlay headline */
  overlayHeadline?: string;
  /** Page id */
  pageId?: Content.Components['GenericProductModuleDto']['pageId'];
  /** Parameters */
  parameters?: Content.Components['GenericProductModuleDto']['parameters'];
  /** Parent product id */
  parentId?: Content.Components['GenericProductModuleDto']['id'];
  /** Parent product name */
  parentName?: string;
  /** Indicates whether to display the validation error */
  shouldHighlightErrors?: boolean;
  /** Upsell product */
  upsellProduct?: Content.Components['UpsellChildProductDto'];
}

const BookingExtra = ({
  addForAllUpsellProduct,
  className,
  genericProduct,
  groupByProductAllotmentDays = false,
  id,
  isChildProduct = false,
  isFetching = false,
  isGolfPackage = false,
  isLoading = false,
  overlayDescription,
  isMultiGenericProduct = false,
  overlayHeadline,
  pageId,
  parameters,
  parentId,
  parentName,
  shouldHighlightErrors = false,
  upsellProduct,
}: BookingExtraProps) => {
  const { t } = useI18n();
  const isLaptop = useWindowSize('laptop');
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const { brand, isInWebview } = usePageDetailsContext();
  const [showMoreModal, setShowMoreModal] = useState(false);
  const [showSelectModal, setShowSelectModal] = useState(false);
  const [showCustomSelectModal, setShowCustomSelectModal] = useState(false);
  const [localOptions, setLocalOptions] = useState<SelectOption[]>([]);
  const [selectedOption, setSelectedOption] = useState<string>('none');
  const hasTag = genericProduct?.tag && genericProduct?.tag.tagText !== '';

  const categoryIds = groupByProductAllotmentDays
    ? []
    : isMultiGenericProduct || genericProduct?.containsOutHomeProducts
      ? []
      : genericProduct?.categoryId
        ? [genericProduct.categoryId]
        : [];

  const getInvalidationKeys = () => {
    if (isGolfPackage) return [QueryId.GOLF_PACKAGE];

    if (isChildProduct) return [QueryId.PARENT_PRODUCT];

    if (isMultiGenericProduct) return [QueryId.MULTI_GENERIC_PRODUCT];

    return [QueryId.GENERIC_PRODUCT];
  };

  const invalidationKeys = getInvalidationKeys();

  const identifiers = {
    bookingStateId: parameters?.bookingStateId ?? '',
    id:
      (isChildProduct || isMultiGenericProduct || isGolfPackage
        ? parentId
        : id) ?? '',
    pageId: pageId ?? '',
  };

  const {
    data: dataPatchPreBookingProduct,
    isLoading: isMutatingPatchPreBookingProduct,
    mutateAsync: mutateAsyncPatchPreBookingProduct,
  } = useMutationPatchPreBookingProduct();

  // Generate localOptions
  useEffect(() => {
    const productsToMap = groupByProductAllotmentDays
      ? genericProduct?.availableProductsForAllPaxByDate
      : genericProduct?.availableProductsForAllPax;

    const options: SelectOption[] = [
      {
        name: t('items.modules.bookingFlow.noneSelected'),
        value: 'none',
      },
      ...(productsToMap?.map(product => ({
        id: product.id,
        name: product.headline,
        value: product.id,
      })) ?? []),
    ];

    setLocalOptions(options);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    genericProduct?.availableProductsForAllPax,
    genericProduct?.availableProductsForAllPaxByDate,
    groupByProductAllotmentDays,
  ]);

  // Check for selected option
  useEffect(() => {
    const productsToCheck = groupByProductAllotmentDays
      ? genericProduct?.availableProductsForAllPaxByDate
      : genericProduct?.availableProductsForAllPax;

    if (productsToCheck && productsToCheck.length > 0) {
      setSelectedOption(
        productsToCheck.find(product => product.isSelectedByAll)?.id ?? 'none',
      );
    }
  }, [
    genericProduct?.availableProductsForAllPax,
    genericProduct?.availableProductsForAllPaxByDate,
    groupByProductAllotmentDays,
  ]);

  useEffect(() => {
    if (!dataPatchPreBookingProduct) return;

    if (!dataPatchPreBookingProduct.result) {
      setErrorMessage(dataPatchPreBookingProduct.message ?? null);
    } else {
      setErrorMessage(null);
    }
  }, [dataPatchPreBookingProduct]);

  const handleEdit = () => {
    if (groupByProductAllotmentDays) {
      setShowCustomSelectModal(true);

      return;
    }

    setShowSelectModal(true);
  };

  const handleChooseIndividually = () => {
    setErrorMessage(null);

    if (groupByProductAllotmentDays) {
      setShowCustomSelectModal(true);

      return;
    }

    setShowSelectModal(true);
  };

  const handleAddForAllSingleSelect = async () => {
    const productsForAllPax = groupByProductAllotmentDays
      ? genericProduct?.availableProductsForAllPaxByDate
      : genericProduct?.availableProductsForAllPax;

    if (!productsForAllPax) return;

    const patchProductBody =
      generatePatchProductBodyRequestAddForAllSingleSelect(
        productsForAllPax,
        categoryIds,
      );

    await mutateAsyncPatchPreBookingProduct({
      brand,
      data: patchProductBody,
      invalidationKeys: invalidationKeys,
      parameters: identifiers,
    });
  };

  const handleAddForAllMultipleSelect = async (
    event: React.ChangeEvent<HTMLSelectElement>,
  ) => {
    setSelectedOption(event.target.value);

    if (event.target.value === 'none' && genericProduct?.passengers) {
      const patchProductBody =
        generatePatchProductBodyRequestDeleteForAllMultipleSelect(
          genericProduct.passengers,
        );

      await mutateAsyncPatchPreBookingProduct({
        brand,
        data: patchProductBody,
        invalidationKeys: invalidationKeys,
        parameters: identifiers,
      });

      return;
    }

    const productsSource = groupByProductAllotmentDays
      ? (genericProduct?.availableProductsForAllPaxByDate ?? [])
      : (genericProduct?.availableProductsForAllPax ?? []);

    const availableProductsForPassengers = getAvailableProductsForPassengers(
      productsSource,
      event.target.value,
    );

    const patchProductBody =
      generatePatchProductBodyRequestAddForAllMultipleSelect(
        availableProductsForPassengers,
        categoryIds,
      );

    await mutateAsyncPatchPreBookingProduct({
      brand,
      data: patchProductBody,
      invalidationKeys: invalidationKeys,
      parameters: identifiers,
    });
  };

  const handleOnClickSaveCustom = async (
    passengers: Content.Components['GenericProductPassengerDto'][],
  ) => {
    const patchProductBody = getSelectionChangesMultipleChoice(
      passengers,
      genericProduct?.passengersWithProductSelected ?? [],
      [],
    );

    if (!patchProductBody) return;

    await mutateAsyncPatchPreBookingProduct({
      brand,
      data: patchProductBody,
      invalidationKeys: invalidationKeys,
      parameters: identifiers,
    });
  };

  const handleAddForAllUpsell = () => {
    addForAllUpsellProduct?.();
  };

  if (isLoading) {
    return (
      <div
        className={classNames(styles.component, styles.isLoading, className)}
      >
        <div className={styles.mainWrapper}>
          <div className={styles.contentWrapper}>
            <div className={styles.topSection}>
              <SkeletonLoader
                className={styles.loader}
                height="25px"
                width="35%"
              />

              <SkeletonLoader
                className={styles.loader}
                height="15px"
                width="98%"
              />

              <SkeletonLoader
                className={styles.loader}
                height="15px"
                width="93%"
              />

              <SkeletonLoader
                className={styles.loader}
                height="15px"
                width="96%"
              />
            </div>

            <div className={styles.bottomSection}>
              <SkeletonLoader
                className={styles.loader}
                height="54px"
                width="100%"
              />

              <SkeletonLoader
                className={styles.loader}
                height="54px"
                width="100%"
              />
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (isInWebview) {
    return (
      <BookingExtraSelectModal
        categoryIds={categoryIds}
        choiceType={genericProduct?.choiceType}
        containsOutHomeProducts={genericProduct?.containsOutHomeProducts}
        description={overlayDescription}
        genericProduct={genericProduct}
        headline={overlayHeadline}
        identifiers={identifiers}
        invalidationKeys={invalidationKeys}
        isInWebview
        isOpen
        onClose={() => setShowSelectModal(false)}
        passengers={genericProduct?.passengers ?? []}
      />
    );
  }

  return (
    <div
      className={classNames(styles.component, className, {
        [styles.isMultiSelectGenericProduct]: isMultiGenericProduct,
      })}
    >
      <div
        className={classNames(styles.mainWrapper, {
          [styles.hasTag]: hasTag,
        })}
      >
        {hasTag && (
          <Tag
            className={styles.tag}
            text={genericProduct?.tag.tagText}
            variant={genericProduct?.tag.tagType as TagVariant}
          />
        )}

        {genericProduct?.images &&
          genericProduct?.images.length > 0 &&
          genericProduct?.variant === 'normal' && (
            <div className={styles.imageWrapper}>
              <ImageSlider
                ignoreMinimumSize
                images={
                  genericProduct?.images
                    ? genericProduct.images.map(img => ({
                        altText: img.image.altText,
                        url: img.image.url,
                      }))
                    : []
                }
              />
            </div>
          )}

        {errorMessage && <AlertBanner text={errorMessage} variant="warning" />}

        <div className={styles.contentWrapper}>
          <div className={styles.topSection}>
            <div className={styles.headlineWrapper}>
              <Typography
                className={styles.headline}
                tag="h3"
                tagStyle="headingSmall"
              >
                {genericProduct?.headline}
              </Typography>

              {shouldHighlightErrors && (
                <Typography
                  className={styles.error}
                  text={`*${t('items.modules.bookingFlow.required')}`}
                />
              )}
            </div>

            {genericProduct?.description &&
              genericProduct?.variant === 'normal' && (
                <TruncatedText
                  onShowMoreClick={() => setShowMoreModal(true)}
                  richText={genericProduct.description}
                  showMoreText={t('items.modules.bookingFlow.readMore')}
                />
              )}
          </div>

          <div className={styles.bottomSection}>
            {isMutatingPatchPreBookingProduct || (!isLoading && isFetching) ? (
              <React.Fragment>
                <SkeletonLoader
                  className={styles.loader}
                  height={isLaptop ? '54px' : '34px'}
                  width="100%"
                />

                <SkeletonLoader
                  className={styles.loader}
                  height="54px"
                  width="100%"
                />
              </React.Fragment>
            ) : (
              <React.Fragment>
                <div className={styles.priceWrapper}>
                  <PriceItems priceItems={genericProduct?.priceItems || []} />

                  {/* if addForAllPax is true you cannot edit the choice individually for each passenger */}
                  {(genericProduct?.state === 'fullSelected' ||
                    genericProduct?.state === 'partialSelected' ||
                    genericProduct?.state === 'included') &&
                    !genericProduct?.addProductForAllPax &&
                    genericProduct?.isInteractable && (
                      <Link
                        className={styles.link}
                        onClick={() => handleEdit()}
                      >
                        <Typography tagStyle="bodyMedium">
                          {t('items.modules.bookingFlow.edit')}
                        </Typography>
                      </Link>
                    )}
                </div>

                {genericProduct?.state === 'parentSelected' && (
                  <div className={styles.selectedActionWrapper}>
                    <div className={styles.passengersWrapper}>
                      <SelectedPassengers
                        passengers={genericProduct?.passengers
                          ?.filter(passenger => passenger.isSelected)
                          .map(passenger => passenger.passenger)}
                      />
                    </div>

                    <AlertPill
                      text={`${t('items.modules.bookingFlow.includedIn')} ${parentName}`}
                      variant="success"
                    />
                  </div>
                )}

                {(genericProduct?.state === 'fullSelected' ||
                  genericProduct?.state === 'partialSelected' ||
                  genericProduct?.state === 'included') && (
                  <div className={styles.selectedActionWrapper}>
                    <div className={styles.passengersWrapper}>
                      <SelectedPassengers
                        passengers={genericProduct?.passengers
                          ?.filter(passenger => passenger.isSelected)
                          .map(passenger => passenger.passenger)}
                      />
                    </div>

                    {genericProduct?.addProductForAllPax ? (
                      <Select
                        disabled={!genericProduct?.isInteractable}
                        label={t('items.modules.bookingFlow.yourChoice')}
                        onChange={handleAddForAllMultipleSelect}
                        options={localOptions}
                        value={selectedOption}
                      />
                    ) : (
                      <AlertPill
                        text={
                          genericProduct.state === 'included'
                            ? t('items.modules.bookingFlow.included')
                            : genericProduct.isInWishlist
                              ? t('items.modules.myTripWishlist.inBasket')
                              : t('items.modules.bookingFlow.added')
                        }
                        variant="success"
                      />
                    )}
                  </div>
                )}

                {genericProduct?.state === 'unselected' && (
                  <div className={styles.unselectedActionWrapper}>
                    {/* if addForAllPax is true you cannot choose individually for each passenger */}
                    {!genericProduct?.addProductForAllPax &&
                      genericProduct?.isInteractable && (
                        <Link
                          className={styles.link}
                          onClick={() => handleChooseIndividually()}
                        >
                          <Typography tagStyle="bodyMedium">
                            {t('items.modules.bookingFlow.chooseIndividually')}
                          </Typography>
                        </Link>
                      )}

                    {groupByProductAllotmentDays ? (
                      <Select
                        disabled={!genericProduct?.isInteractable}
                        label={t('items.modules.bookingFlow.selectForAll')}
                        onChange={handleAddForAllMultipleSelect}
                        options={localOptions}
                        selectedLabel={t(
                          'items.modules.bookingFlow.selectedForAll',
                        )}
                        value={selectedOption}
                      />
                    ) : genericProduct?.choiceType === 'single' ? (
                      <Button
                        disabled={!genericProduct?.isInteractable}
                        onClick={() => handleAddForAllSingleSelect()}
                        tabIndex={-1}
                        text={t('items.modules.bookingFlow.addForAll')}
                        variant="secondary"
                      />
                    ) : (
                      <Select
                        disabled={!genericProduct?.isInteractable}
                        label={t('items.modules.bookingFlow.selectForAll')}
                        onChange={handleAddForAllMultipleSelect}
                        options={localOptions}
                        selectedLabel={t(
                          'items.modules.bookingFlow.selectedForAll',
                        )}
                        value={selectedOption}
                      />
                    )}
                  </div>
                )}
              </React.Fragment>
            )}
          </div>
        </div>
      </div>

      {upsellProduct && (
        <div className={styles.upsellProductWrapper}>
          <div className={styles.topSection}>
            <div className={styles.upsellHeadlineWrapper}>
              <Typography
                className={styles.headline}
                tag="h3"
                tagStyle="headingSmall"
              >
                {upsellProduct.headline}
              </Typography>

              {upsellProduct?.tag && upsellProduct?.tag.tagText !== '' && (
                <Tag
                  text={upsellProduct?.tag.tagText}
                  variant={upsellProduct?.tag.tagType as TagVariant}
                />
              )}
            </div>

            {upsellProduct.description && (
              <Typography
                className={styles.description}
                tag="p"
                tagStyle="bodyMedium"
              >
                {upsellProduct.description}
              </Typography>
            )}
          </div>

          <div className={styles.bottomSection}>
            <PriceItems
              priceItems={
                upsellProduct?.priceItem ? [upsellProduct.priceItem] : []
              }
            />

            <div className={styles.unselectedActionWrapper}>
              <Button
                onClick={() => handleAddForAllUpsell()}
                tabIndex={-1}
                text={t('items.modules.bookingFlow.addForAll')}
                variant="secondary"
              />
            </div>
          </div>
        </div>
      )}

      <ExtraCardModal
        description={genericProduct?.description}
        headline={genericProduct?.headline}
        images={genericProduct?.images ?? []}
        isOpen={showMoreModal}
        onClose={() => setShowMoreModal(false)}
        priceItems={
          genericProduct?.priceItems
            ? genericProduct.priceItems.map(item => ({
                label: item.label ?? null,
                price: item.price ?? null,
                tooltip: null,
              }))
            : []
        }
      />

      <BookingExtraSelectModal
        categoryIds={categoryIds}
        choiceType={genericProduct?.choiceType}
        containsOutHomeProducts={genericProduct?.containsOutHomeProducts}
        description={overlayDescription}
        genericProduct={genericProduct}
        headline={overlayHeadline}
        identifiers={identifiers}
        invalidationKeys={invalidationKeys}
        isOpen={showSelectModal}
        onClose={() => setShowSelectModal(false)}
        passengers={genericProduct?.passengers ?? []}
      />

      <BookingExtraCustomSelectModal
        description={overlayDescription}
        headline={overlayHeadline}
        isOpen={showCustomSelectModal}
        onClickSave={handleOnClickSaveCustom}
        onClose={() => setShowCustomSelectModal(false)}
        passengers={genericProduct?.passengers ?? []}
      />
    </div>
  );
};

BookingExtra.displayName = 'BookingExtra';

export default BookingExtra;
